import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAnimations } from '../contexts/AnimationContext'

const AnimationToggle: React.FC = () => {
  const { settings, updateSettings } = useAnimations()

  const handleToggle = () => {
    updateSettings({ enabled: !settings.enabled })
  }

  const handlePresetChange = (preset: 'performance' | 'balanced' | 'cinematic' | 'minimal' | 'snow') => {
    updateSettings({ preset })
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed top-4 right-4 z-50 bg-gray-900/80 backdrop-blur-sm rounded-lg p-4 border border-gray-700/50"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Main Toggle */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-white text-sm font-medium">Animations</span>
          <motion.button
            onClick={handleToggle}
            className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
              settings.enabled ? 'bg-purple-500' : 'bg-gray-600'
            }`}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm"
              animate={{
                x: settings.enabled ? 20 : 0
              }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          </motion.button>
        </div>

        {/* Quality Presets */}
        <AnimatePresence>
          {settings.enabled && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-2"
            >
              <div className="text-xs text-gray-400 mb-2">Quality</div>
              <div className="flex flex-wrap gap-1">
                {(['minimal', 'performance', 'balanced', 'cinematic', 'snow'] as const).map((preset) => (
                  <motion.button
                    key={preset}
                    onClick={() => handlePresetChange(preset)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      settings.preset === preset
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {preset.charAt(0).toUpperCase() + preset.slice(1)}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Performance Info */}
        <AnimatePresence>
          {settings.enabled && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-3 pt-3 border-t border-gray-700/50"
            >
              <div className="text-xs text-gray-400 space-y-1">
                <div>Stars: {settings.particleCount}</div>
                <div>FPS: {settings.animationSpeed === 1 ? '60' : settings.animationSpeed * 60}</div>
                {settings.reduceMotion && (
                  <div className="text-yellow-400">Reduced Motion</div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}

export default AnimationToggle